import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import Skeleton from '@mui/material/Skeleton';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  addQcComment,
  addQcIssue,
  dismissQcApproval,
  replaceQcIssue,
  seedQcComments,
  seedQcCycle,
  seedQcIssues,
  setQcIssueStatus,
} from '../../../app/store/assessmentWorkspaceSlice';
import {
  addWorkflowQcComment,
  createWorkflowQcIssue,
  getQCReviewData,
  getWorkflowQcComments,
  getWorkflowQcIssues,
  getWorkflowQcReviewCycle,
  updateQcIssueStatus,
} from '../../../services/qcReview';
import { QcIssueCategory, QcIssueStatus } from '../../../interfaces/qcIssue.interface';
import { QCReviewItem } from '../../../interfaces/qcReviewData.interface';
import {
  countQcIssuesByStatus,
  getQcApprovalProgress,
  getWorkspaceCapabilities,
  groupQcCommentsByQuestion,
  groupQcIssuesByQuestion,
  questionMatchesStatusFilters,
  toQcAuthor,
} from '../../Assessments/Workspace/workspaceDerivations';
import QcReviewPanel from '../../Assessments/Workspace/QcReviewPanel';
import QcStatusTiles from '../../Assessments/Workspace/QcStatusTiles';
import AdminRoundReturnedModal from '../../Assessments/Workspace/AdminRoundReturnedModal';
import QcApprovedNextStepsModal from '../../Assessments/Workspace/QcApprovedNextStepsModal';
import { WorkspaceTraceContext } from '../../Assessments/Workspace/WorkspaceTraceContext';
import QcQuestionSnapshot from './QcQuestionSnapshot';
import { ACCENT_BLUE, ACCENT_BLUE_DARK } from '../../Assessments/Workspace/qcTheme';

// The full QC loop from the Manage Assessments grid: the workspace's components beside a read-only answer snapshot, scoped to questions with QC activity.

const Header = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '20px',
  padding: '20px 26px 14px',
  borderBottom: '1px solid rgba(0,0,0,0.12)',
});

const Title = styled('h2')({
  fontSize: '1.35rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.87)',
  margin: 0,
});

const MetaRow = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  gap: '18px',
  flexWrap: 'wrap',
  marginTop: '10px',
});

const MetaLabel = styled('span')({
  fontSize: '0.65rem',
  letterSpacing: '0.8px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.5)',
  textTransform: 'uppercase',
  marginRight: '6px',
});

const MetaValue = styled('span')({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.85)',
});

const MetaSub = styled('span')({
  fontSize: '0.78rem',
  color: 'rgba(0,0,0,0.55)',
  marginLeft: '6px',
});

const HeaderRight = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  flex: 'none',
});

const CloseButton = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  display: 'inline-flex',
  cursor: 'pointer',
  color: 'rgba(0,0,0,0.55)',
  '&:hover': { color: 'rgba(0,0,0,0.8)' },
});

const QuestionStrip = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '12px 26px',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
});

const QuestionCode = styled('span')({
  fontSize: '0.95rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.85)',
});

const PagerCluster = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  flex: 'none',
});

const PagerCount = styled('span')({
  fontSize: '0.8125rem',
  color: 'rgba(0,0,0,0.55)',
});

const PagerLink = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  color: ACCENT_BLUE,
  cursor: 'pointer',
  '&:disabled': { color: 'rgba(0,0,0,0.3)', cursor: 'default' },
  '& .MuiSvgIcon-root': { fontSize: '18px' },
});

const Body = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  flexWrap: 'wrap',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
});

const AnswerColumn = styled('div')({
  flex: '1 1 460px',
  minWidth: 0,
  padding: '20px 26px 26px',
});

const QcColumn = styled('div')({
  flex: '1 1 460px',
  boxSizing: 'border-box',
  minWidth: 0,
  borderLeft: '1px solid rgba(0,0,0,0.12)',
  padding: '20px 22px 26px',
});

// Mirrors the loaded strip and two columns so the modal keeps its shape while the bundles land.
function LoadingBody() {
  return (
    <>
      <QuestionStrip>
        <Skeleton variant='text' width={320} height={22} />
        <PagerCluster>
          <Skeleton variant='text' width={96} />
          <Skeleton variant='text' width={120} />
        </PagerCluster>
      </QuestionStrip>
      <Body>
        <AnswerColumn>
          <Skeleton variant='text' width='45%' height={18} />
          <Skeleton variant='rounded' height={92} sx={{ mt: 1.5 }} />
          <Skeleton variant='rounded' height={220} sx={{ mt: 2 }} />
        </AnswerColumn>
        <QcColumn>
          <Skeleton variant='text' width='35%' height={18} />
          <Skeleton variant='rounded' height={120} sx={{ mt: 1.5 }} />
          <Skeleton variant='rounded' height={120} sx={{ mt: 1.5 }} />
        </QcColumn>
      </Body>
    </>
  );
}

const EmptyState = styled('div')({
  flex: 1,
  padding: '60px 26px',
  textAlign: 'center',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.55)',
});

const Footer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '14px 26px',
  borderTop: '1px solid rgba(0,0,0,0.12)',
});

const FooterCaption = styled('span')({
  fontSize: '0.8125rem',
  color: 'rgba(0,0,0,0.55)',
});

const FooterActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginLeft: 'auto',
});

const OpenAssessmentButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  background: '#fff',
  color: ACCENT_BLUE,
  border: '1px solid rgba(25,118,210,0.5)',
  borderRadius: '4px',
  padding: '9px 16px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: 'rgba(25,118,210,0.04)', borderColor: ACCENT_BLUE },
  '& .MuiSvgIcon-root': { fontSize: '16px' },
});

const CloseFooterButton = styled('button')({
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '9px 20px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: ACCENT_BLUE_DARK },
});

export interface ContinueQcReviewRow {
  workflowId?: number;
  assessmentId?: number;
  survey: number;
  vendorName: string;
  siteOrApplicationName: string;
  assessorName: string;
  assessorLevel: string;
  qcRound: number | null;
}

export interface ContinueQcReviewModalProps {
  open: boolean;
  row: ContinueQcReviewRow | null;
  onClose: () => void;
}

export default function ContinueQcReviewModal({ open, row, onClose }: ContinueQcReviewModalProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.user);
  const {
    qcIssuesByQuestion, qcCommentsByQuestion, qcReviewSubmitted,
    qcRoundNumber, qcRoundClosed, qcApprovalDismissed, qcAssessmentApproved,
  } = useAppSelector(state => state.assessmentWorkspace);
  const [items, setItems] = useState<QCReviewItem[]>([]);
  const [index, setIndex] = useState(0);
  const [statusFilters, setStatusFilters] = useState<QcIssueStatus[]>([]);

  // One tile can stand for several statuses, so on means any of them and off clears them all.
  const toggleStatusFilter = useCallback((statuses: QcIssueStatus[]) => {
    setStatusFilters(prev => (statuses.some(status => prev.includes(status))
      ? prev.filter(status => !statuses.includes(status))
      : [...prev, ...statuses]));
    setIndex(0);
  }, []);
  const [loaded, setLoaded] = useState(false);
  const [adminRoundInfo, setAdminRoundInfo] = useState<{ returned: number; approved: number } | null>(null);
  const [traceText, setTraceText] = useState<string | null>(null);
  const trace = useMemo(() => ({ traceText, setTraceText }), [traceText]);

  const workflowId = row?.workflowId;

  useEffect(() => {
    if (!open || !workflowId) return;
    setLoaded(false);
    setIndex(0);
    setStatusFilters([]);
    // One bundle per surface: answers/sub-controls, issues, threads, cycle state.
    Promise.all([
      getQCReviewData(workflowId),
      getWorkflowQcIssues(workflowId),
      getWorkflowQcComments(workflowId),
      getWorkflowQcReviewCycle(workflowId),
    ])
      .then(([reviewItems, issues, comments, cycle]) => {
        setItems(reviewItems);
        dispatch(seedQcIssues(groupQcIssuesByQuestion(issues)));
        dispatch(seedQcComments(groupQcCommentsByQuestion(comments)));
        dispatch(seedQcCycle(cycle));
        setLoaded(true);
      })
      .catch(error => { console.error('Failed to load QC review data', error); });
  }, [open, workflowId, dispatch]);

  // The modal walks only the questions that carry QC activity, narrowed to the tiles in play.
  const qcActiveItems = useMemo(
    () => items.filter(item => (qcIssuesByQuestion[item.question.id] || []).length > 0),
    [items, qcIssuesByQuestion],
  );
  const qcItems = useMemo(
    () => qcActiveItems.filter(item => questionMatchesStatusFilters(item.question.id, statusFilters, qcIssuesByQuestion)),
    [qcActiveItems, qcIssuesByQuestion, statusFilters],
  );
  const current = qcItems[Math.min(index, Math.max(0, qcItems.length - 1))];

  const allIssues = useMemo(() => Object.values(qcIssuesByQuestion).flat(), [qcIssuesByQuestion]);
  const tileCounts = useMemo(() => countQcIssuesByStatus(allIssues), [allIssues]);

  const userName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || '' : '';
  const currentAuthor = toQcAuthor(userName, user?.type);
  const qcProgress = getQcApprovalProgress(allIssues);
  const allApproved = qcReviewSubmitted && qcProgress.total > 0 && qcProgress.approved === qcProgress.total;
  const capabilities = getWorkspaceCapabilities(
    user?.type, qcReviewSubmitted ? 'qc_submitted' : 'submitted', qcReviewSubmitted,
    qcRoundClosed, qcAssessmentApproved, allApproved,
  );

  const handleSetStatus = useCallback((issueId: number, status: QcIssueStatus) => {
    if (!current) return;
    dispatch(setQcIssueStatus({ questionId: current.question.id, issueId, status, by: currentAuthor.name }));
    if (issueId > 0 && workflowId) {
      updateQcIssueStatus(issueId, status)
        .then(() => getWorkflowQcReviewCycle(workflowId))
        .then(cycle => { dispatch(seedQcCycle(cycle)); })
        .catch(error => { console.error('Failed to update QC issue status', error); });
    }
    // Same round-resolution moment as the workspace: last Ready item resolved with returns pops the modal (all-approved auto-opens Approve Assessment below).
    if (!['approved', 'needs_update'].includes(status)) return;
    const counted = allIssues.filter(issue => issue.status !== 'first_pass');
    const stillReady = counted.some(issue => issue.id !== issueId && issue.status === 'ready_for_tpn_review');
    const returned = counted.filter(issue => issue.id !== issueId && issue.status === 'needs_update').length
      + (status === 'needs_update' ? 1 : 0);
    if (!stillReady && returned > 0) {
      setAdminRoundInfo({
        returned,
        approved: counted.filter(issue => (issue.id === issueId ? status === 'approved' : issue.status === 'approved')).length,
      });
    }
  }, [current, workflowId, currentAuthor, allIssues, dispatch]);

  const handlePostQcComment = useCallback(async (text: string) => {
    if (!current || !workflowId) return;
    dispatch(addQcComment({ questionId: current.question.id, text, author: currentAuthor }));
    try {
      await addWorkflowQcComment(workflowId, current.question.id, text);
    } catch (error) {
      console.error('Failed to post QC comment', error);
    }
  }, [current, workflowId, currentAuthor, dispatch]);

  const handleAddQcIssue = useCallback(async (category: QcIssueCategory, text: string) => {
    if (!current || !workflowId) return;
    const questionId = current.question.id;
    const tempId = -Date.now();
    dispatch(addQcIssue({ questionId, category, text, author: currentAuthor, id: tempId }));
    try {
      const created = await createWorkflowQcIssue(workflowId, questionId, category, text);
      dispatch(replaceQcIssue({ questionId, issueId: tempId, issue: created }));
    } catch (error) {
      console.error('Failed to add QC issue', error);
    }
  }, [current, workflowId, currentAuthor, dispatch]);

  // A cross-reference can point outside the filtered walk, so following one clears the filter.
  const handleNavigateToCode = useCallback((shortCode: string) => {
    const target = qcItems.findIndex(item => item.question.shortCode === shortCode);
    if (target !== -1) {
      setIndex(target);
      return;
    }
    const unfiltered = qcActiveItems.findIndex(item => item.question.shortCode === shortCode);
    if (unfiltered === -1) return;
    setStatusFilters([]);
    setIndex(unfiltered);
  }, [qcItems, qcActiveItems]);

  const openAssessment = () => {
    if (!row?.assessmentId) return;
    const questionParam = current ? `?question=${current.question.id}` : '';
    window.open(
      `${window.location.origin}/assessment/${row.assessmentId}/questionnaire/${row.survey}${questionParam}`,
      '_blank',
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label='Continue QC review'
      PaperProps={{ sx: { width: '94vw', maxWidth: 1440, height: '92vh', borderRadius: '4px', display: 'flex', flexDirection: 'column' } }}
      maxWidth={false}
    >
      <Header>
        <div style={{ minWidth: 0 }}>
          <Title>Continue QC Review</Title>
          <MetaRow>
            <span><MetaLabel>Vendor</MetaLabel><MetaValue>{row?.vendorName}</MetaValue></span>
            <span><MetaLabel>Site or App</MetaLabel><MetaValue>{row?.siteOrApplicationName}</MetaValue></span>
            <span>
              <MetaLabel>Assessor</MetaLabel>
              <MetaValue>{row?.assessorName}</MetaValue>
              <MetaSub>{row?.assessorLevel}</MetaSub>
            </span>
            {!!row?.qcRound && <span><MetaLabel>Round</MetaLabel><MetaValue>{row.qcRound}</MetaValue></span>}
          </MetaRow>
        </div>
        <HeaderRight>
          {loaded ? (
            <QcStatusTiles
              counts={tileCounts}
              selected={statusFilters}
              showFirstPass={capabilities.firstPassVisible}
              onToggle={toggleStatusFilter}
            />
          ) : (
            // Counts read 0 until the issues land, which would state the opposite of the truth.
            <Skeleton variant='rounded' width={430} height={62} sx={{ alignSelf: 'center' }} />
          )}
          <CloseButton type='button' aria-label='Close continue QC review' onClick={onClose}>
            <CloseIcon sx={{ fontSize: 22 }} />
          </CloseButton>
        </HeaderRight>
      </Header>
      {!loaded && <LoadingBody />}
      {loaded && qcItems.length === 0 && (
        <EmptyState>
          {statusFilters.length
            ? 'No questions match the selected statuses.'
            : 'No QC activity on this assessment yet.'}
        </EmptyState>
      )}
      {current && (
        <>
          <QuestionStrip>
            <QuestionCode>
              {current.question.shortCode ? `${current.question.shortCode} · ` : ''}
              {current.question.title}
            </QuestionCode>
            <PagerCluster>
              <PagerCount>Question {Math.min(index, qcItems.length - 1) + 1} of {qcItems.length}</PagerCount>
              <PagerLink type='button' disabled={index <= 0} onClick={() => setIndex(i => Math.max(0, i - 1))}>
                <ChevronLeftIcon />
                Previous Question
              </PagerLink>
              <PagerLink
                type='button'
                disabled={index >= qcItems.length - 1}
                onClick={() => setIndex(i => Math.min(qcItems.length - 1, i + 1))}
              >
                Next Question
                <ChevronRightIcon />
              </PagerLink>
            </PagerCluster>
          </QuestionStrip>
          <Body>
            <WorkspaceTraceContext.Provider value={trace}>
              <AnswerColumn>
                <QcQuestionSnapshot item={current} />
              </AnswerColumn>
              <QcColumn>
                <QcReviewPanel
                  issues={qcIssuesByQuestion[current.question.id] || []}
                  comments={qcCommentsByQuestion[current.question.id] || []}
                  entries={[]}
                  questionId={current.question.id}
                  questionCode={current.question.shortCode || current.question.title}
                  capabilities={capabilities}
                  qcStageVisible
                  qcReviewSubmitted={qcReviewSubmitted}
                  currentAuthor={currentAuthor}
                  currentRound={qcRoundNumber}
                  onSetStatus={handleSetStatus}
                  onNavigateToCode={handleNavigateToCode}
                  onPostQcComment={handlePostQcComment}
                  onAddQcIssue={handleAddQcIssue}
                />
              </QcColumn>
            </WorkspaceTraceContext.Provider>
          </Body>
        </>
      )}
      <Footer>
        <FooterCaption>
          {qcItems.length} question{qcItems.length === 1 ? '' : 's'}
          {statusFilters.length ? ' matching the selected statuses' : ' with QC activity on this assessment'}
        </FooterCaption>
        <FooterActions>
          {!!row?.assessmentId && (
            <OpenAssessmentButton type='button' onClick={openAssessment}>
              <OpenInNewIcon />
              Open Assessment
            </OpenAssessmentButton>
          )}
          <CloseFooterButton type='button' onClick={onClose}>Close</CloseFooterButton>
        </FooterActions>
      </Footer>
      <AdminRoundReturnedModal
        open={adminRoundInfo !== null}
        returnedCount={adminRoundInfo?.returned || 0}
        approvedCount={adminRoundInfo?.approved || 0}
        roundNumber={qcRoundNumber}
        onClose={() => setAdminRoundInfo(null)}
      />
      <QcApprovedNextStepsModal
        open={allApproved && !qcApprovalDismissed && !qcAssessmentApproved}
        approvedCount={qcProgress.approved}
        unitLabel={row?.siteOrApplicationName}
        onGoToAssessments={() => {
          // The assessments grid sits right behind this modal — close both; Finalize Assessment in the row's Actions completes the review.
          dispatch(dismissQcApproval());
          onClose();
        }}
        onClose={() => dispatch(dismissQcApproval())}
      />
    </Dialog>
  );
}
